/**
 * Google's own sign-in button, and the one place its script is loaded.
 *
 * Google Identity Services renders the button itself — the markup, the wordmark
 * and the consent flow are theirs, and a hand-built imitation is both against
 * their branding terms and a worse experience. What this component owns is the
 * lifecycle: load the script once, render into a div, and hand the resulting
 * credential upwards.
 *
 * **No token handling happens beyond this file.** The credential Google returns
 * is passed straight to `POST /auth/google` and never stored, never put in
 * `localStorage`, and never read. What the browser keeps afterwards is the
 * portal's own HTTP-only session cookie, exactly as it always has been — the
 * cookie is set by the server and this code cannot see it.
 */

import { useEffect, useRef, useState } from "react";

const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

/** The slice of Google Identity Services this file uses. */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        hosted_domain?: string;
        auto_select?: boolean;
      }) => void;
      renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

/**
 * Load Google's script once per document, however many components ask for it.
 *
 * Resolving from a module-level promise rather than a component state matters
 * on a re-mount: React strict mode mounts twice in development, and appending
 * the tag twice would initialise the library twice against the same client id.
 */
let scriptPromise: Promise<void> | null = null;

function loadGoogleScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      // Let a later attempt retry rather than caching the failure forever —
      // this is usually a network blip or an ad blocker, both recoverable.
      scriptPromise = null;
      reject(new Error("Google sign-in could not be loaded."));
    });
    if (!existing) document.head.appendChild(script);
  });

  return scriptPromise;
}

interface Props {
  clientId: string;
  /** Passed to Google so its own account chooser filters to the Workspace. */
  hostedDomain: string;
  onCredential: (credential: string) => void;
  onError: (message: string) => void;
}

export function GoogleSignInButton({ clientId, hostedDomain, onCredential, onError }: Props) {
  const target = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);

  // The callback is held in a ref so that re-rendering the parent — which
  // happens on every keystroke of the password form beside it — does not
  // re-initialise Google's library with a new function identity.
  const callback = useRef(onCredential);
  callback.current = onCredential;
  const errorCallback = useRef(onError);
  errorCallback.current = onError;

  useEffect(() => {
    let cancelled = false;

    loadGoogleScript()
      .then(() => {
        if (cancelled || !target.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callback.current(response.credential),
          // A hint to Google's chooser, never a security control: the portal
          // re-checks the `hd` claim server-side, because anything decided in
          // the browser is decided by whoever holds the browser.
          hosted_domain: hostedDomain,
          auto_select: false,
        });
        window.google.accounts.id.renderButton(target.current, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          shape: "rectangular",
          width: 320,
        });
        setLoading(false);
      })
      .catch((caught: Error) => {
        if (cancelled) return;
        setLoading(false);
        errorCallback.current(caught.message);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, hostedDomain]);

  return (
    <div className="flex flex-col items-center gap-sm">
      <div ref={target} />
      {loading && <p className="text-body-sm text-muted">Loading Google sign-in…</p>}
    </div>
  );
}
